import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Loader2, FileText } from "lucide-react";

/**
 * Pay-run detail — drill into one payrun, see per-employee
 * gross/net/tax + super contributions. Backed by
 * `GET /api/integrations/keypay/clients/{cid}/payruns/{pid}`.
 */
export default function KeyPayPayrunDetailPage() {
  const { clientId, payrunId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/integrations/keypay/clients/${clientId}/payruns/${payrunId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load payrun"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, payrunId]);

  const s = data?.summary || {};
  const totals = data?.totals || {};
  const rows = data?.employees || [];

  return (
    <div className="p-6 max-w-7xl" data-testid="keypay-payrun-detail">
      <Link to={`/integrations/keypay/clients/${clientId}`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="keypay-payrun-back">
        <ArrowLeft className="h-3 w-3" /> Back to dashboard
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-purple-100 text-purple-700">
          <FileText className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">KEYPAY · PAYRUN DETAIL</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            Pay run #{payrunId}
            {s.is_finalised && (
              <Badge className="ml-2 align-middle rounded-sm text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">FINALISED</Badge>
            )}
          </h1>
          <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
            Paid: {fmtDate(s.date_paid)} · Period: {fmtDate(s.pay_period_starting)} → {fmtDate(s.pay_period_ending)}
          </div>
        </div>
      </div>

      {loading ? (
        <Card className="rounded-sm border-zinc-200"><CardContent className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Tile label="Gross"  value={fmtMoney(totals.gross)}  accent="text-emerald-700" />
            <Tile label="Tax"    value={fmtMoney(totals.tax)}    accent="text-red-700" />
            <Tile label="Super"  value={fmtMoney(totals.super)}  accent="text-amber-700" />
            <Tile label="Net"    value={fmtMoney(totals.net)}    accent="text-zinc-900" />
          </div>

          <Card className="rounded-sm border-zinc-200" data-testid="keypay-payrun-rows">
            <CardContent className="p-0">
              <div className="grid grid-cols-[1.4fr_70px_110px_110px_110px_110px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
                <span>Employee</span>
                <span className="text-right">Hours</span>
                <span className="text-right">Gross</span>
                <span className="text-right">Tax</span>
                <span className="text-right">Super</span>
                <span className="text-right">Net</span>
              </div>
              {rows.length === 0 ? (
                <div className="p-8 text-center text-sm italic text-zinc-500">
                  No payslip rows on this pay run.
                </div>
              ) : rows.map((r) => (
                <Link key={r.employee_id}
                  to={`/integrations/keypay/clients/${clientId}/employees/${r.employee_id}`}
                  className="grid grid-cols-[1.4fr_70px_110px_110px_110px_110px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50"
                  data-testid={`keypay-payrun-row-${r.employee_id}`}>
                  <span className="truncate font-semibold">{r.employee_name}</span>
                  <span className="font-mono text-right text-xs text-zinc-600">{(r.hours_worked || 0).toFixed(2)}</span>
                  <span className="font-mono text-right text-emerald-700">{fmtMoney(r.gross)}</span>
                  <span className="font-mono text-right text-red-700">{fmtMoney(r.tax)}</span>
                  <span className="font-mono text-right text-amber-700">{fmtMoney(r.super)}</span>
                  <span className="font-mono text-right font-semibold">{fmtMoney(r.net)}</span>
                </Link>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white">
      <div className="label-overline text-[9px]">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}
function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n); if (!Number.isFinite(v)) return "—";
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(s) { return s ? String(s).slice(0, 10) : "—"; }
